<template>
  <div>
    <div class="documents" data-locator="documents-page" :style="{ 'left' : pageOffsetForNavDrawer }">
      <ColResizer class="fill-height"
                  :width="documentsSidebarWidth"
                  :min-width="documentsSidebarMinMaxWidths.min"
                  :max-width="documentsSidebarMinMaxWidths.max"
                  @resize="onSidebarWidthChanged"
      >
        <template v-if="!$vuetify.display.xs" #left>
          <div class="documents__sidebar-wrapper">
            <DocumentsSidebar class="documents__sidebar" data-locator="documents-sidebar"/>
          </div>
        </template>
        <template #right>
          <div class="documents__list-wrapper">
            <div class="documents__list">
              <DocumentsHeader
                :mnemo="roomMnemo"
                :actionsDisabled="isEmptyStateOnRootFolder"
              />
              <template v-if="isEmptyStateOnRootFolder">
                <DocumentsStorageExceededAlert v-if="isDocumentsStorageExceededAlertDisplayed"/>
                <DocumentsEmptyState :mnemo="roomMnemo"/>
              </template>
              <template v-else>
                <DocumentsStorageExceededAlert v-if="isDocumentsStorageExceededAlertDisplayed"/>
                <DocumentsTableTemplate :mnemo="roomMnemo"/>
              </template>
            </div>
          </div>
        </template>
      </ColResizer>
    </div>

    <DocumentsPane/>
    <DocumentsTablePane :mnemo="roomMnemo"/>
    <DocumentsFiltersPane/>
    <DocumentsVersioningPane :mnemo="roomMnemo"/>
    <DocumentsAISummaryPane />

    <DocumentsMoveSnackbar v-if="movementSnackbarIsOpen"/>
    <DocumentsNumberingSnackbar v-if="numberingSnackbarIsOpen"/>

    <DocumentsDeleteDialog
      v-if="documentsDeleteDialogIsOpen"
      @close="CLOSE_DOCUMENTS_DELETION_DIALOG"
      @validate-deletion="onDeletionValidation"
    />
    <DocumentsNewFolderDialog/>
    <DocumentsFileUploadDialog
      v-if="documentsUploadFilesDialogIsOpen"
      :mnemo="roomMnemo"
      @close="CLOSE_DOCUMENTS_UPLOAD_FILES"
    />
    <DocumentsDownloadFilesDialog
      v-if="documentsDownloadFilesDialogIsOpen"
      :mnemo="roomMnemo"
      :fileType="currentPossibleFileType"
      @close="CLOSE_DOCUMENTS_DOWNLOAD_FILES"
    />
    <DocumentsRenameDialog :key="documentToRename.id"/>
    <DocumentsLogsDialog :mnemo="roomMnemo"/>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import ColResizer from '@/common/col-resizer/ColResizer.vue'
import { BROADCAST, DEFAULT_PAGE_SIZE } from '@/common/constants'
import { PROJECT_NAV_DRAWER_WIDTHS } from '@/common/layout/constants'
import { getRoomRouteType } from '@/common/utils/rooms'
import { ROOM_TYPES } from '@/common/utils/roomTypes'
import { toast } from '@/common/utils/toast'
import { getUserSetting, setUserSetting } from '@/common/utils/userSettings'
import { RECENT_FOLDER, ROOT_FOLDER } from '@/project/documents/constants/special-folders'
import { ROOM_MEMBERS_ROUTE_NAME } from '@/router'
import {
  GET_PAGINATED_DOCUMENTS,
  GET_PAGINATED_DOCUMENTS_FOR_FILE,
  LOAD_FOLDERS_TREE,
  RESET_DOCUMENTS,
  RESET_FULL_TREE,
  GET_FOLDER_PATH,
} from '@/store/modules/documents/action_types'
import {
  SET_DOCUMENTS_PAGINATION,
  SET_MOVEMENT_SNACKBAR_IS_OPEN,
  SET_PAGINATED_DOCUMENTS_CURRENT_FOLDER_ID,
} from '@/store/modules/documents/mutation_types'
import { DocumentsBreadcrumbActionTypes } from '@/store/modules/documents-breadcrumb/action_types'
import { DocumentsBreadcrumbMutationTypes } from '@/store/modules/documents-breadcrumb/mutation_types'
import { DocumentsDeleteActionTypes } from '@/store/modules/documents-delete/action_types'
import { CLOSE_DOCUMENTS_DOWNLOAD_FILES } from '@/store/modules/documents-download-files/action_types'
import { CLOSE_DOCUMENTS_UPLOAD_FILES } from '@/store/modules/documents-upload-files/action_types'
import { GET_ROOM_STATS } from '@/store/modules/room/action_types'

import DocumentsDeleteDialog from './dialogs/DocumentsDeleteDialog.vue'
import DocumentsDownloadFilesDialog, { FILE_TYPES } from './dialogs/DocumentsDownloadFilesDialog.vue'
import DocumentsFileUploadDialog from './dialogs/DocumentsFileUploadDialog.vue'
import DocumentsLogsDialog from './dialogs/DocumentsLogsDialog.vue'
import DocumentsNewFolderDialog from './dialogs/DocumentsNewFolderDialog.vue'
import DocumentsRenameDialog from './dialogs/DocumentsRenameDialog.vue'
import DocumentsEmptyState from './DocumentsEmptyState.vue'
import DocumentsFiltersPane from './DocumentsFiltersPane.vue'
import DocumentsHeader from './DocumentsHeader.vue'
import DocumentsMoveSnackbar from './DocumentsMoveSnackbar.vue'
import DocumentsNumberingSnackbar from './DocumentsNumberingSnackbar.vue'
import DocumentsStorageExceededAlert from './DocumentsStorageExceededAlert.vue'
import DocumentsSidebar from './navigation-sidebar/DocumentsSidebar.vue'
import DocumentsAISummaryPane from './pane/DocumentsAISummaryPane.vue'
import DocumentsPane from './pane/DocumentsPane.vue'
import DocumentsTablePane from './pane/DocumentsTablePane.vue'
import DocumentsVersioningPane from './pane/DocumentsVersioningPane.vue'
import DocumentsTableTemplate from './table/DocumentsTableTemplate.vue'

const DOCUMENTS_SIDEBAR_WIDTH_KEY = 'documents-sidebar-width'

export default {
  name: 'Documents',
  components: {
    ColResizer,
    DocumentsAISummaryPane,
    DocumentsDeleteDialog,
    DocumentsDownloadFilesDialog,
    DocumentsEmptyState,
    DocumentsFileUploadDialog,
    DocumentsFiltersPane,
    DocumentsHeader,
    DocumentsLogsDialog,
    DocumentsMoveSnackbar,
    DocumentsNewFolderDialog,
    DocumentsNumberingSnackbar,
    DocumentsPane,
    DocumentsRenameDialog,
    DocumentsSidebar,
    DocumentsStorageExceededAlert,
    DocumentsTablePane,
    DocumentsTableTemplate,
    DocumentsVersioningPane,
  },
  provide () {
    return {
      loadDocumentsTable: this.loadDocumentsTable,
    }
  },
  head () {
    return {
      title: this.$t('project.documents.Documents.metaTitle'),
    }
  },
  data () {
    return {
      documentRedactionChannel: null,
    }
  },
  computed: {
    ...mapState('documents', [
      'documents',
      'movementSnackbarIsOpen',
      'numberingSnackbarIsOpen',
      'fullTree',
      'loading',
      'paginatedDocuments',
      'documentsPagination',
      'paginatedDocumentsPending',
      'paginatedDocumentsCurrentFolderId',
      'searchModeEnabled',
      'foldersTree',
    ]),
    ...mapState('documentsDelete', {
      documentsDeleteDialogIsOpen: 'isOpen',
      documentsToDelete: 'documents',
    }),
    ...mapState('documentsRename', ['documentToRename']),
    ...mapState('room', ['currentRoom', 'roomStats', 'roomStorageExceededDialogIsOpen']),
    ...mapState('documentsBreadcrumb', ['folderNavigationToken']),
    ...mapGetters('room', ['storagePercent', 'isStorageFull', 'roomMnemo']),
    ...mapGetters('documentsBreadcrumb', ['hasTodoFolderOrChild']),
    ...mapGetters('user', ['currentUserId']),
    ...mapState('documentsUploadFiles', {
      documentsUploadFilesDialogIsOpen: 'isOpen',
    }),
    ...mapState('documentsDownloadFiles', {
      documentsDownloadFilesDialogIsOpen: 'isOpen',
    }),
    ...mapState('global', ['mini']),
    isDocumentsStorageExceededAlertDisplayed () {
      return (this.currentRoom?.type === ROOM_TYPES.DATAROOM_UNLIMITED && this.isStorageFull) ||
      (this.currentRoom?.type === ROOM_TYPES.DATAROOM && this.roomStats?.storage && this.storagePercent >= 80)
    },
    currentPossibleFileType () {
      return this.hasTodoFolderOrChild
        ? FILE_TYPES.TODO
        : FILE_TYPES.DOCUMENT
    },
    pageOffsetForNavDrawer () {
      if (this.$vuetify.display.mobile) {
        return 0
      }

      if (this.mini) {
        return PROJECT_NAV_DRAWER_WIDTHS.WITH_UNIT.FOLDED
      }

      return PROJECT_NAV_DRAWER_WIDTHS.WITH_UNIT.REGULAR
    },
    documentsSidebarMinMaxWidths () {
      switch (true) {
        case this.$vuetify.display.xs:
          return { min: 200, max: 240 }
        case this.$vuetify.display.sm:
          return { min: 200, max: 280 }
        case this.$vuetify.display.md:
          return { min: 200, max: 320 }
        default:
          return { min: 240, max: 480 }
      }
    },
    documentsSidebarWidth () {
      const { min: sidebarMinWidth, max: sidebarMaxWidth } = this.documentsSidebarMinMaxWidths
      const storedWidth = !isNaN(+getUserSetting(this.currentUserId, DOCUMENTS_SIDEBAR_WIDTH_KEY))
      ? +getUserSetting(this.currentUserId, DOCUMENTS_SIDEBAR_WIDTH_KEY)
      : sidebarMinWidth

      // We do not handle width for mobile and xs-screen because documents sidebar don't expand with these modes
      const navDrawerExpandedWidth = PROJECT_NAV_DRAWER_WIDTHS.RAW.REGULAR
      const navDrawerFoldedWidth = PROJECT_NAV_DRAWER_WIDTHS.RAW.FOLDED
      const navDrawerCurrentWidth = this.mini
        ? navDrawerFoldedWidth
        : navDrawerExpandedWidth
      const availableSpaceWhenFolded = navDrawerExpandedWidth - navDrawerCurrentWidth
      const fillingSpaceWidth = storedWidth + availableSpaceWhenFolded

      return Math.min(Math.max(sidebarMinWidth, fillingSpaceWidth), sidebarMaxWidth)
    },
    isEmptyStateOnRootFolder () {
      return (
          this.paginatedDocumentsCurrentFolderId === 0
          || (
            this.paginatedDocumentsCurrentFolderId === this.foldersTree.todoFolderId ||
            this.paginatedDocumentsCurrentFolderId === RECENT_FOLDER.id
          )
        )
        && !this.paginatedDocumentsPending
        && !this.searchModeEnabled
        && (
          this.paginatedDocuments.length === 0
          || (
            this.paginatedDocuments.length === 1 &&
            this.paginatedDocuments[0].specialFolder === 'todo' &&
            !this.foldersTree.hasAccessToTodoFolder
          )
        )
    },
  },
  created () {
    if (this.currentRoom?.hideDocuments) {
      this.$router.replace({ name: ROOM_MEMBERS_ROUTE_NAME, params: { roomType: getRoomRouteType(this.currentRoom) } })
    }
    this.GET_GROUPS(this.roomMnemo)
    this.SET_MOVEMENT_SNACKBAR_IS_OPEN(false)
  },
  async mounted () {
    this.initPaginationFromUserSettings()
    await this.loadDocumentsTable(this.$route.query.displayedFile, this.$route.query.displayedFolder)
    this.prepareGetRoomStats()
    await this.syncBreadcrumbWithRouteQuery(this.$route.query.displayedFolder)
    // Create BroadcastChannel to listen for document redaction events
    // It refreshes the documents table when redaction is completed
    this.setupDocumentRedactionChannel()
  },
  unmounted () {
    this.SET_PAGINATED_DOCUMENTS_CURRENT_FOLDER_ID(ROOT_FOLDER.id)
  },
  beforeUnmount () {
    if (this.documentRedactionChannel) {
      this.documentRedactionChannel.close()
    }
  },
  watch: {
    async roomStorageExceededDialogIsOpen (dialogIsOpened) {
      if (!dialogIsOpened) {
        await this.prepareGetRoomStats()
        this.loadDocumentsTable()
      }
    },
    async '$route.query' (newQuery) {
      if (newQuery.displayedFile || newQuery.displayedFolder) {
        await this.syncBreadcrumbWithRouteQuery(newQuery.displayedFolder)
        await this.loadDocumentsTable(newQuery.displayedFile, newQuery.displayedFolder)
      }
    },
  },
  methods: {
    ...mapActions('documents', [
      LOAD_FOLDERS_TREE,
      RESET_DOCUMENTS,
      RESET_FULL_TREE,
      GET_PAGINATED_DOCUMENTS,
      GET_PAGINATED_DOCUMENTS_FOR_FILE,
      GET_FOLDER_PATH,
    ]),
    ...mapActions('documentsBreadcrumb', [
      DocumentsBreadcrumbActionTypes.ADD_BREADCRUMB_ITEM, 
      DocumentsBreadcrumbActionTypes.SET_BREADCRUMB_ITEMS,
    ]),
    ...mapActions('groups', ['GET_GROUPS']),
    ...mapActions('documentsDelete', [DocumentsDeleteActionTypes.CLOSE_DOCUMENTS_DELETION_DIALOG, DocumentsDeleteActionTypes.DELETE_FILDERS]),
    ...mapActions('documentsUploadFiles', [CLOSE_DOCUMENTS_UPLOAD_FILES]),
    ...mapActions('documentsDownloadFiles', [CLOSE_DOCUMENTS_DOWNLOAD_FILES]),
    ...mapActions('room', [GET_ROOM_STATS]),
    ...mapMutations('documentsBreadcrumb', [DocumentsBreadcrumbMutationTypes.UPDATE_FOLDER_NAVIGATION_TOKEN]),
    ...mapMutations('documents', [SET_DOCUMENTS_PAGINATION, SET_PAGINATED_DOCUMENTS_CURRENT_FOLDER_ID, SET_MOVEMENT_SNACKBAR_IS_OPEN]),
    async syncBreadcrumbWithRouteQuery (displayedFolderIdParam) {
      // Invalidate previous navigations to avoid races
      this.UPDATE_FOLDER_NAVIGATION_TOKEN()
      const token = this.folderNavigationToken

      // Set breadcrumb if the navigation is the latest one
      const setBreadcrumbIfLatest = (items) => {
        if (token !== this.folderNavigationToken) return
        this.SET_BREADCRUMB_ITEMS(items)
      }

      const parseFolderId = (value) => {
        if (value === undefined || value === null || value === '') return null
        const id = Number.parseInt(value, 10)
        return Number.isNaN(id) ? null : id
      }

      const displayedFolderId = parseFolderId(displayedFolderIdParam)
      const rootItem = {
        id: ROOT_FOLDER.id,
        name: this.$t('common.documents.rootFolder'),
      }
      // If no folder is displayed, or root folder is displayed, set breadcrumb to root
      if (displayedFolderId === null || displayedFolderId === 0 || displayedFolderId === ROOT_FOLDER.id) {
        setBreadcrumbIfLatest([rootItem])
        return
      }
      try {
        const folderPath = await this.GET_FOLDER_PATH(displayedFolderId)
        setBreadcrumbIfLatest([rootItem].concat(folderPath || []))
      } catch (error) {
        console.error(error)
        setBreadcrumbIfLatest([rootItem])
      }
    },
    async loadDocumentsTable (displayedFileIdParam, displayedFolderIdParam) {
      const displayedFileId = parseInt(displayedFileIdParam)
      const displayedFolderId = parseInt(displayedFolderIdParam)

      try {
        if (displayedFolderId >= 0) {
          if (displayedFileId) {
            await this.GET_PAGINATED_DOCUMENTS_FOR_FILE({ fileId: displayedFileId, folderId: displayedFolderId })
          } else {
            await this.GET_PAGINATED_DOCUMENTS(displayedFolderId)
          }
          return
        }
        await this.GET_PAGINATED_DOCUMENTS()
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.documents.Documents.getDocumentsError'))
      }
    },
    initPaginationFromUserSettings() {
      const pageSize = getUserSetting(
        this.currentUserId,
        'documents-items-per-page',
      )

      this.updatePagination({
        pageSize: pageSize || DEFAULT_PAGE_SIZE,
        page: 1,
      })
    },
    updatePagination(changes) {
      this.SET_DOCUMENTS_PAGINATION({ ...this.documentsPagination, ...changes })
    },
    async onDeletionValidation () {
      const needFoldersTreeRefreshAfterDeletion = this.documentsToDelete.some(document => document.type === 'folder')
      await this.DELETE_FILDERS()

      if (needFoldersTreeRefreshAfterDeletion) {
        await this.LOAD_FOLDERS_TREE()
      }
    },
    onSidebarWidthChanged (width) {
      setUserSetting(this.currentUserId, DOCUMENTS_SIDEBAR_WIDTH_KEY, width)
    },
    async prepareGetRoomStats () {
      try {
        await this.GET_ROOM_STATS(this.roomMnemo)
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.project-settings.ProjectSettingsDataRoomInfo.getRoomStatsError'))
      }
    },
    setupDocumentRedactionChannel () {
      this.documentRedactionChannel = new BroadcastChannel(BROADCAST.CHANNELS.DOCUMENT_REDACTION)
      this.documentRedactionChannel.onmessage = (event) => {
        if (event.data?.type === BROADCAST.MESSAGES.DOCUMENT_REDACTION_UPDATED) {
          this.loadDocumentsTable()

          toast.success(this.$t('project.documents.DocumentViewer.RedactDocumentDialog.redactDocumentSuccess'))
        }
      }
    },
  },
  beforeRouteLeave (to, from, next) {
    this.RESET_DOCUMENTS()
    this.RESET_FULL_TREE()
    next()
  },
}
</script>

<style lang="scss" scoped>
.documents {
  --header-height: 64px;

  height: calc(100% - var(--header-height));
  position: fixed;
  right: 0;

  &__sidebar,
  &__list {
    padding: 16px 24px;
  }

  &__sidebar-wrapper,
  &__list-wrapper {
    position: relative;
    overflow-y: auto;
    height: 100%;
    overflow-x: hidden;
  }

  &__list-wrapper {
    border-left: 1px solid rgb(var(--v-theme-grey-lighten-4));
  }

  &__list {
    position: absolute;
    top: 0;
    width: 100%;
  }
}
</style>
